import { NavLink } from "react-router-dom";
import { Home, Film, Tv, Clapperboard, Server, LogOut, Projector, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LIBRARY = [
  { to: "/", label: "Home", icon: Home, id: "home", end: true },
  { to: "/movies", label: "Movies", icon: Film, id: "movies" },
  { to: "/shows", label: "TV Shows", icon: Tv, id: "shows" },
  { to: "/videos", label: "All videos", icon: Clapperboard, id: "videos" },
];
const SOURCES = { to: "/sources", label: "Sources", icon: Server, id: "sources" };
const USERS = { to: "/users", label: "Users", icon: Users, id: "users" };
const tools = (user) => [SOURCES, ...(user?.role === "admin" ? [USERS] : [])];

const Item = ({ item }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end} data-testid={`nav-item-${item.id}`}
      className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
        isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"}`}>
      <Icon size={16} strokeWidth={1.75} />
      <span className="flex-1">{item.label}</span>
    </NavLink>
  );
};

const Label = ({ children }) => (
  <p className="mb-2 mt-8 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{children}</p>
);

export const Brand = ({ size = "text-xl" }) => (
  <div className="flex items-center gap-2">
    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-900 text-white"><Projector size={16} /></div>
    <span className={`font-heading ${size} font-bold tracking-tight`}>Cucoloris Action Harbor</span>
  </div>
);

export const Sidebar = () => {
  const { user, logout } = useAuth();
  return (
    <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-zinc-200 bg-[#F6F5F0]/80 px-4 pb-6 pt-7 backdrop-blur-md md:flex" data-testid="sidebar">
      <div className="px-3"><Brand /></div>
      <div className="mt-2 flex-1 overflow-y-auto">
        <Label>Library</Label>
        <nav className="space-y-0.5">{LIBRARY.map((i) => <Item key={i.id} item={i} />)}</nav>
        <Label>Manage</Label>
        <nav className="space-y-0.5">{tools(user).map((i) => <Item key={i.id} item={i} />)}</nav>
      </div>
      <div className="flex items-center justify-between border-t border-zinc-200 px-3 pt-4">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] uppercase text-amber-800">{user?.role === "admin" ? "admin" : user?.shared_from ? "family" : "member"}</span>
          </p>
          <p className="truncate font-mono text-[11px] text-zinc-500">{user?.email}</p>
        </div>
        <button onClick={logout} data-testid="logout-button" title="Log out"
          className="rounded-md p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"><LogOut size={16} /></button>
      </div>
    </aside>
  );
};

export const MobileNav = () => {
  const { user, logout } = useAuth();
  return (
    <nav className="sticky top-0 z-30 flex gap-1 overflow-x-auto border-b border-zinc-200 bg-white/90 px-3 py-2 backdrop-blur md:hidden" data-testid="mobile-nav">
      {[...LIBRARY, ...tools(user)].map((i) => (
        <NavLink key={i.id} to={i.to} end={i.end} data-testid={`mobile-nav-${i.id}`}
          className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1 text-xs ${isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{i.label}</NavLink>
      ))}
      <button onClick={logout} data-testid="mobile-logout-button" className="whitespace-nowrap rounded-full px-3 py-1 text-xs text-zinc-600">Log out</button>
    </nav>
  );
};
